import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Process } from '../components/Process';
import { ConsultationForm } from '../components/ConsultationForm';
import { Shield, CheckCircle2, Award, FileText, Scale, Clock, DollarSign, Target, Globe, Lock, Bell, Eye, Calendar, Phone } from 'lucide-react';
import { testimonials } from '../data/mock';
import { allServicesData } from '../data/servicesData';

const iconMap = {
  Shield, CheckCircle2, Award, FileText, Scale, Clock, DollarSign, Target, Globe, Lock, Bell, Eye, Calendar, Phone
};

export const ServiceTemplate = ({ serviceKey }) => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const service = allServicesData[serviceKey];
  
  const openForm = (formType) => {
    setIsFormOpen(true);
  };
  
  if (!service) {
    return <div>Service not found</div>;
  }

  // Use service-specific button text from data, or fallback to "Get Started"
  const buttonText = service.buttonText || 'Get Started';

  const serviceTestimonials = testimonials.filter(t => [1, 2, 3].includes(t.id));

  return (
    <div className="min-h-screen bg-white">
      <Navbar onOpenForm={openForm} />
      
      <ConsultationForm 
        isOpen={isFormOpen} 
        onClose={() => setIsFormOpen(false)}
        formType="registration"
      />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-[#0A1F44] to-[#153356] relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#D4AF37]/10 rounded-full blur-3xl"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <div className="inline-flex items-center space-x-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full px-4 py-2 mb-6">
              <Shield className="w-5 h-5 text-[#D4AF37]" />
              <span className="text-[#D4AF37] text-sm font-medium">{service.category}</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              {service.hero.title}
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              {service.hero.subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="#pricing" className="liquid inline-block bg-[#D4AF37] hover:bg-[#B8941F] text-[#0A1F44] font-bold py-4 px-8 rounded-lg text-lg whitespace-nowrap">
                View Pricing
              </a>
              <button 
                onClick={() => openForm('consultation')}
                className="liquid-white inline-block border-2 border-white text-white hover:bg-white hover:text-[#0A1F44] font-bold py-4 px-8 rounded-lg text-lg whitespace-nowrap"
              >
                Free Consultation
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* What Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#D4AF37] font-semibold text-sm uppercase tracking-wider">What is {service.title}</span>
              <h2 className="text-4xl font-bold text-[#0A1F44] mt-4 mb-6">
                {service.what.title}
              </h2>
              {service.what.content.map((paragraph, index) => (
                <p key={index} className="text-lg text-gray-600 leading-relaxed mb-6">
                  {paragraph}
                </p>
              ))}
              <div className="bg-[#D4AF37]/10 border-l-4 border-[#D4AF37] p-6 rounded">
                <p className="text-[#0A1F44] font-semibold">
                  "{service.what.quote}"
                </p>
              </div>
            </div>
            <div className="bg-gradient-to-br from-[#0A1F44] to-[#153356] rounded-2xl p-8 text-white">
              <h3 className="text-2xl font-bold mb-6">Key Features</h3>
              <ul className="space-y-4">
                {service.benefits.slice(0, 6).map((benefit, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <CheckCircle2 className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-1" />
                    <div>
                      <div className="font-semibold">{benefit.title}</div>
                      <div className="text-gray-300 text-sm">{benefit.desc}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Slider */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#D4AF37] font-semibold text-sm uppercase tracking-wider">Benefits</span>
            <h2 className="text-4xl font-bold text-[#0A1F44] mt-4 mb-6">
              Why Choose Our {service.title}?
            </h2>
          </div>
          
          <div className="relative overflow-hidden">
            <div className="flex gap-6 animate-scroll hover:pause-animation">
              {service.benefits.map((benefit, index) => {
                const Icon = iconMap[benefit.icon] || Shield;
                return (
                  <div key={index} className="flex-shrink-0 w-80 bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all">
                    <div className="w-14 h-14 bg-[#D4AF37] rounded-xl flex items-center justify-center mb-4">
                      <Icon className="w-7 h-7 text-[#0A1F44]" />
                    </div>
                    <h3 className="text-lg font-bold text-[#0A1F44] mb-2">{benefit.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{benefit.desc}</p>
                  </div>
                );
              })}
              {/* Duplicate for seamless loop */}
              {service.benefits.slice(0, 4).map((benefit, index) => {
                const Icon = iconMap[benefit.icon] || Shield;
                return (
                  <div key={`dup-${index}`} className="flex-shrink-0 w-80 bg-white rounded-2xl p-6 shadow-md hover:shadow-xl transition-all">
                    <div className="w-14 h-14 bg-[#D4AF37] rounded-xl flex items-center justify-center mb-4">
                      <Icon className="w-7 h-7 text-[#0A1F44]" />
                    </div>
                    <h3 className="text-lg font-bold text-[#0A1F44] mb-2">{benefit.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{benefit.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* First CTA */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative bg-gradient-to-br from-red-900/40 via-gray-900 to-red-900/40 rounded-3xl p-1">
            <div className="bg-black rounded-3xl overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-2 items-center">
                <div className="relative h-64 lg:h-80 overflow-hidden">
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                    <div className="relative w-64 h-64">
                      <div className="absolute inset-0 bg-gradient-to-br from-gray-300 to-gray-400 rounded-full opacity-90"></div>
                      <div className="absolute inset-4 bg-gradient-to-br from-gray-400 to-gray-500 rounded-full opacity-80"></div>
                      <div className="absolute inset-8 bg-gradient-to-br from-gray-500 to-gray-600 rounded-full opacity-70"></div>
                      <div className="absolute inset-12 bg-gradient-to-br from-gray-600 to-gray-700 rounded-full opacity-60"></div>
                      <div className="absolute inset-16 bg-gradient-to-br from-gray-700 to-gray-800 rounded-full opacity-50"></div>
                    </div>
                  </div>
                </div>
                <div className="px-8 py-12 lg:py-16">
                  <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 leading-tight">
                    Ready to Get Started?
                  </h2>
                  <p className="text-gray-300 text-lg mb-8 leading-relaxed">
                    Protect your intellectual property with expert legal guidance. Our experienced attorneys are ready to help.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <button 
                      onClick={() => openForm('registration')}
                      className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold px-8 py-4 rounded-xl text-lg transition-all duration-300 whitespace-nowrap"
                    >
                      {buttonText}
                    </button>
                    <button 
                      onClick={() => openForm('consultation')}
                      className="border-2 border-white text-white hover:bg-white hover:text-black font-bold px-8 py-4 rounded-xl text-lg transition-all duration-300 whitespace-nowrap"
                    >
                      Free Consultation
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#D4AF37] font-semibold text-sm uppercase tracking-wider">Pricing</span>
            <h2 className="text-4xl font-bold text-[#0A1F44] mt-4 mb-6">
              {service.title} Pricing
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Transparent pricing with no hidden fees. Choose the package that fits your needs.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {service.pricing.map((plan, index) => (
              <div
                key={index}
                className={`bg-white rounded-2xl p-8 transition-all duration-300 flex flex-col ${
                  plan.popular
                    ? 'border-4 border-[#D4AF37] shadow-2xl transform md:-translate-y-4'
                    : 'border-2 border-gray-200 shadow-lg hover:shadow-2xl'
                }`}
              >
                {plan.popular && (
                  <div className="inline-block bg-[#D4AF37] text-[#0A1F44] px-4 py-1 rounded-full font-bold text-sm mb-4 whitespace-nowrap">
                    MOST POPULAR
                  </div>
                )}
                <h3 className="text-2xl font-bold text-[#0A1F44] mb-2">{plan.name}</h3>
                <div className="mb-4">
                  <span className="text-5xl font-bold text-[#0A1F44]">{plan.price}</span>
                  {!plan.price.includes('Custom') && <span className="text-gray-600 text-lg"></span>}
                </div>
                <p className="text-gray-600 mb-6">{plan.description}</p>
                <ul className="space-y-3 mb-8 flex-grow">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start space-x-3">
                      <CheckCircle2 className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <button 
                  onClick={() => openForm('registration')}
                  className="liquid w-full bg-[#D4AF37] hover:bg-[#B8941F] text-[#0A1F44] font-bold py-3 px-6 rounded-lg whitespace-nowrap mt-auto"
                >
                  {plan.price === 'Custom' ? 'Contact Sales' : buttonText}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#D4AF37] font-semibold text-sm uppercase tracking-wider">How It Works</span>
            <h2 className="text-4xl font-bold text-[#0A1F44] mt-4 mb-6">
              Simple 3-Step Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our streamlined process makes {service.title.toLowerCase()} easy and efficient.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {service.process.map((step, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all">
                <div className="w-16 h-16 bg-[#0A1F44] rounded-2xl flex items-center justify-center mb-6">
                  <FileText className="w-8 h-8 text-[#D4AF37]" />
                </div>
                <h3 className="text-2xl font-bold text-[#0A1F44] mb-4">{step.title}</h3>
                <p className="text-gray-600 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#0A1F44] mb-6">
              What Our Clients Say
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {serviceTestimonials.map((testimonial) => (
              <div key={testimonial.id} className="bg-gray-50 rounded-2xl p-6">
                <p className="text-gray-700 mb-4">"{testimonial.content}"</p>
                <div className="flex items-center space-x-3">
                  <img src={testimonial.image} alt={testimonial.name} className="w-12 h-12 rounded-full object-cover" />
                  <div>
                    <div className="font-bold text-[#0A1F44]">{testimonial.name}</div>
                    <div className="text-gray-600 text-sm">{testimonial.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Second CTA */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative bg-gradient-to-br from-[#D4AF37]/30 via-[#0A1F44] to-[#D4AF37]/30 rounded-3xl p-1">
            <div className="bg-black rounded-3xl overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-2 items-center">
                <div className="px-8 py-12 lg:py-16 order-2 lg:order-1">
                  <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 leading-tight">
                    Protect Your Rights Today
                  </h2>
                  <p className="text-gray-300 text-lg mb-8 leading-relaxed">
                    Don't wait to secure your intellectual property. Get expert legal assistance from experienced attorneys.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <button 
                      onClick={() => openForm('registration')}
                      className="bg-gradient-to-r from-[#D4AF37] to-[#B8941F] hover:from-[#B8941F] hover:to-[#D4AF37] text-[#0A1F44] font-bold px-8 py-4 rounded-xl text-lg transition-all duration-300 whitespace-nowrap"
                    >
                      {buttonText}
                    </button>
                    <button 
                      onClick={() => openForm('consultation')}
                      className="border-2 border-white text-white hover:bg-white hover:text-[#0A1F44] font-bold px-8 py-4 rounded-xl text-lg transition-all duration-300 whitespace-nowrap"
                    >
                      Free Consultation
                    </button>
                  </div>
                </div>
                <div className="relative h-64 lg:h-80 overflow-hidden order-1 lg:order-2">
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                    <div className="relative w-64 h-64">
                      <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37] to-[#B8941F] rounded-full opacity-30"></div>
                      <div className="absolute inset-4 bg-gradient-to-br from-[#D4AF37]/80 to-[#0A1F44] rounded-full opacity-40"></div>
                      <div className="absolute inset-8 bg-gradient-to-br from-[#0A1F44] to-[#153356] rounded-full opacity-50"></div>
                      <div className="absolute inset-12 bg-gradient-to-br from-[#153356] to-[#0A1F44] rounded-full opacity-60"></div>
                      <div className="absolute inset-16 bg-gradient-to-br from-[#0A1F44] to-[#1a3a5c] rounded-full opacity-70"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};
